import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    Sparkles, RefreshCw, Star, Moon, Sun, Share2, BrainCircuit, Lock, CheckCircle2, Clock, Unlock,
    Wand2, Scroll, Sprout, Crown, Key, Heart, Shield, Flame, Mountain, Scale, Anchor, Skull, Droplets, Link, Zap, Megaphone, Globe, Leaf, History, ArrowRight, Hand
} from 'lucide-react';
import { TAROT_DECK, TarotCard, ZODIAC_DATA } from '../constants';
import { AppSettings, UserData, ReadingRecord } from '../types';
import { getTodayString, getTimeUntilAvailable } from '../utils/cosmic';
import { useSetMeta } from '../hooks/useSetMeta';
import { playWhisper, playMysticChord, playSparkle } from '../utils/sound';
import { calculateZodiac } from '../utils/calculator';
import CooldownTimer from '../components/CooldownTimer';
import { generateAIContent } from '../services/ai';
import AdSpace from '../components/AdSpace';
import tarotData from '../src/content/tarot.json';

interface TarotPageProps {
  userData?: UserData;
  settings?: AppSettings;
  updateSettings?: (s: Partial<AppSettings>) => void;
}

const getCardIcon = (id: number) => {
    const props = { size: 72, strokeWidth: 1, className: "text-[var(--season-primary)] drop-shadow-[0_0_25px_rgba(255,200,44,0.4)] opacity-90" };
    
    switch(id) {
        case 0: return <Leaf {...props} />;
        case 1: return <Wand2 {...props} />;
        case 2: return <Scroll {...props} />;
        case 3: return <Sprout {...props} />;
        case 4: return <Crown {...props} />;
        case 5: return <Key {...props} />;
        case 6: return <Heart {...props} />;
        case 7: return <Shield {...props} />;
        case 8: return <Flame {...props} />;
        case 9: return <Mountain {...props} />;
        case 10: return <RefreshCw {...props} />;
        case 11: return <Scale {...props} />;
        case 12: return <Anchor {...props} />;
        case 13: return <Skull {...props} />;
        case 14: return <Droplets {...props} />;
        case 15: return <Link {...props} />;
        case 16: return <Zap {...props} />;
        case 17: return <Star {...props} />;
        case 18: return <Moon {...props} />;
        case 19: return <Sun {...props} />;
        case 20: return <Megaphone {...props} />;
        case 21: return <Globe {...props} />;
        default: return <Star {...props} />;
    }
};

const TarotPage: React.FC<TarotPageProps> = ({ userData, settings, updateSettings }) => {
    // Set SEO meta tags for this page
    useSetMeta({
      title: 'Daily Tarot Card Reading - Free Online Tarot Divination | ZodiAge',
      description: 'Get your daily tarot card reading with detailed interpretations. Free personalized tarot divination based on your zodiac sign and energy.',
      ogImage: 'https://zodiage.com/logo.png'
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentCard, setCurrentCard] = useState<TarotCard | null>(null);
  const [isReversed, setIsReversed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const [isDaily, setIsDaily] = useState(false);
  const [reflection, setReflection] = useState<string>('');
  const [isReflecting, setIsReflecting] = useState(false);
  const [combinedMessage, setCombinedMessage] = useState<string>('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  const [showSpread, setShowSpread] = useState(false);
  const [spreadCards, setSpreadCards] = useState<{card: TarotCard, reversed: boolean}[]>([]);
  const [spreadRevealed, setSpreadRevealed] = useState([false, false, false]);
  const [spreadTimer, setSpreadTimer] = useState('');
  const [isSpreadLocked, setIsSpreadLocked] = useState(false);

  const [reflectionTimer, setReflectionTimer] = useState('');
  const [isReflectionLocked, setIsReflectionLocked] = useState(false);

  useEffect(() => {
    if (settings) {
        const dailyStatus = getTimeUntilAvailable(settings.lastTarotPickTimestamp || null);
        
        if (dailyStatus.locked && settings.dailyCardId !== null) {
            const card = TAROT_DECK.find(c => c.id === settings.dailyCardId) || TAROT_DECK[0];
            setCurrentCard(card);
            setIsReversed(settings.dailyCardReversed || false);
            setIsFlipped(true);
            setIsDaily(true);
        } else {
             setIsDaily(false);
        }

        const spreadStatus = getTimeUntilAvailable(settings.lastSpreadPickTimestamp || null);
        
        if (spreadStatus.locked && settings.lastSpreadCards && settings.lastSpreadCards.length === 3) {
            if (spreadCards.length === 0) {
                const restored = settings.lastSpreadCards.map(s => ({
                    card: TAROT_DECK.find(c => c.id === s.id) || TAROT_DECK[0],
                    reversed: s.reversed
                }));
                setSpreadCards(restored);
                setShowSpread(true);
                setIsSpreadLocked(true);
                // Restore reveal state from settings
                if (settings.lastSpreadRevealed) {
                    setSpreadRevealed(settings.lastSpreadRevealed);
                }
            }
        }

        const reflectionStatus = getTimeUntilAvailable(settings.lastReflectionTimestamp || null);
        if (reflectionStatus.locked && settings.lastReflectionContent) {
            setReflection(settings.lastReflectionContent);
            setIsReflectionLocked(true);
            setReflectionTimer(reflectionStatus.text);
        }
    }

    const updateTimer = () => {
        const dailyStatus = getTimeUntilAvailable(settings?.lastTarotPickTimestamp || null);
        if (dailyStatus.locked) {
            setTimeUntilReset(dailyStatus.text);
            if (!isDaily && settings?.dailyCardId !== null) setIsDaily(true);
        } else {
            setTimeUntilReset('');
            if (isDaily) setIsDaily(false);
        }

        const spreadStatus = getTimeUntilAvailable(settings?.lastSpreadPickTimestamp || null);
        if (spreadStatus.locked) {
            setSpreadTimer(spreadStatus.text);
            setIsSpreadLocked(true);
        } else {
            setSpreadTimer('');
            setIsSpreadLocked(false);
        }

        const reflectionStatus = getTimeUntilAvailable(settings?.lastReflectionTimestamp || null);
        if (reflectionStatus.locked) {
            setReflectionTimer(reflectionStatus.text);
            setIsReflectionLocked(true);
        } else {
            setReflectionTimer('');
            setIsReflectionLocked(false);
        }
    };
    
    updateTimer(); 
    const timer = setInterval(updateTimer, 1000); 

    return () => clearInterval(timer);
    }, [
      settings?.lastTarotPickTimestamp, 
      settings?.dailyCardId, 
      settings?.lastSpreadPickTimestamp, 
      settings?.lastSpreadCards,
      settings?.lastSpreadRevealed,
      settings?.lastReflectionTimestamp,
      settings?.lastReflectionContent,
      spreadCards.length
  ]);

  const drawCard = () => {
    if (isAnimating) return;
    if (isDaily && isFlipped) return; 

    const status = getTimeUntilAvailable(settings?.lastTarotPickTimestamp || null);
    if (status.locked) return;

    if (settings?.soundEnabled) playWhisper();
    setIsAnimating(true);
    
    if (isFlipped && !isDaily) {
      setIsFlipped(false);
      setTimeout(() => randomizeAndReveal(true), 600);
    } else {
      randomizeAndReveal(true);
    }
  };

  const randomizeAndReveal = (markAsDaily: boolean) => {
    let newCard;
    do {
      const randomIndex = Math.floor(Math.random() * TAROT_DECK.length);
      newCard = TAROT_DECK[randomIndex];
    } while (newCard.id === currentCard?.id); 

    const reversed = Math.random() > 0.8; 

    setCurrentCard(newCard);
    setIsReversed(reversed);

    if (settings && updateSettings) {
        const newCollection = settings.tarotCollection.includes(newCard.id) 
            ? settings.tarotCollection 
            : [...settings.tarotCollection, newCard.id];
        
        const newHistoryRecord: ReadingRecord = {
            date: getTodayString(),
            timestamp: Date.now(),
            cardId: newCard.id,
            reversed: reversed
        };
        const newHistory = [newHistoryRecord, ...settings.readingHistory];

        const updates: Partial<AppSettings> = {
            tarotCollection: newCollection,
            readingHistory: newHistory
        };

        if (markAsDaily) {
            const today = getTodayString();
            updates.lastDailyReadingDate = today;
            updates.dailyCardId = newCard.id;
            updates.dailyCardReversed = reversed; 
            updates.lastTarotPickTimestamp = Date.now(); 
            setIsDaily(true);
        }
        updateSettings(updates);
    }

    setTimeout(() => {
      setIsFlipped(true);
      setIsAnimating(false);
      if (settings?.soundEnabled) playMysticChord();
      
      if (userData?.birthDate) {
          generateCombinedMessage(newCard, reversed);
      }
    }, 100);
  };

  const generateCombinedMessage = async (card: TarotCard, reversed: boolean) => {
      if (!userData?.birthDate) return;
      const sign = calculateZodiac(userData.birthDate);
      const mood = settings?.currentMood || 'Neutral';
      
      try {
          const prompt = `
            The user is a ${sign} and feeling ${mood}. 
            They just drew the Tarot card "${card.name}" (${reversed ? 'Reversed' : 'Upright'}).
            Write 2 calm, encouraging sentences on how this card influences this zodiac sign today given their mood.
            Do not predict the future. Focus on energy and vibe.
          `;
          
          const responseText = await generateAIContent(prompt);
          
          if (responseText) {
              setCombinedMessage(responseText);
          }
      } catch (e) { }
  };

  const handleDeepReflection = async () => {
      if (!currentCard) return;

    const status = getTimeUntilAvailable(settings?.lastReflectionTimestamp || null);
      if (status.locked) return;

      setIsReflecting(true);
      
      try {
        const prompt = `Provide a detailed, soulful interpretation of the Tarot card "${currentCard.name}" (${isReversed ? 'Reversed' : 'Upright'}). Discuss its symbolism, astrological connections, and specific advice for someone seeking clarity. 2 paragraphs. Tone: Mystical, wise, empowering.`;

        const responseText = await generateAIContent(prompt);
        if (responseText) {
            setReflection(responseText);
            if (updateSettings) {
                updateSettings({
                    lastReflectionTimestamp: Date.now(),
                    lastReflectionContent: responseText
                });
            }
        }
      } catch (e) {
          setReflection("The stars are clouded right now. Meditate on the card's imagery for your own answer.");
      } finally {
          setIsReflecting(false);
      }
  };

  const handleShare = async () => {
    if (navigator.share && currentCard) {
        try {
            await navigator.share({
                title: 'My Daily Tarot - ZodiAge',
                text: `I drew ${currentCard.name} today on ZodiAge. "${currentCard.meaningUpright}"`,
                url: window.location.href
            });
        } catch (err) {
            setShowShareModal(true);
        }
    } else {
        setShowShareModal(true);
    }
  };

  const handleUnlockSpread = () => {
    const status = getTimeUntilAvailable(settings?.lastSpreadPickTimestamp || null);
      if (status.locked) return;

      const newSpread = [];
      const usedIds = [currentCard?.id];
      
      for(let i=0; i<3; i++) {
          let card;
          do {
             card = TAROT_DECK[Math.floor(Math.random() * TAROT_DECK.length)];
          } while (usedIds.includes(card.id));
          usedIds.push(card.id);
          newSpread.push({ card, reversed: Math.random() > 0.8 });
      }
      
      const initialRevealed = [false, false, false];
      setSpreadCards(newSpread);
      setShowSpread(true);
      setSpreadRevealed(initialRevealed);

      if (updateSettings) {
          updateSettings({
              lastSpreadPickTimestamp: Date.now(),
              lastSpreadCards: newSpread.map(s => ({ id: s.card.id, reversed: s.reversed })),
              lastSpreadRevealed: initialRevealed
          });
      }

      setTimeout(() => {
         const el = document.getElementById('past-present-future');
         if(el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
  };

  const revealSpreadCard = (index: number) => {
      if (spreadRevealed[index]) return;
      const newRevealed = [...spreadRevealed];
      newRevealed[index] = true;
      setSpreadRevealed(newRevealed);
      
      // Persist the revealed state to settings so they stay revealed
      if (updateSettings) {
          updateSettings({
              lastSpreadRevealed: newRevealed
          });
      }
      
      if (settings?.soundEnabled) playSparkle();
  };

  const openHistory = () => {
      setShowHistoryModal(true);
  };

  return (
    <>
    <div className="flex flex-col items-center w-full min-h-[80vh] justify-center py-12 px-4 animate-in fade-in duration-700 relative">

      <div id="tarot-card-section" className="text-center mb-10 relative w-full">
      {isDaily && (
          <div className="flex flex-col items-center gap-2 animate-in slide-in-from-top-4 fade-in mb-6">
              <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-purple-600/50">
                  Today's Magic
              </div>
              <div className="text-sm text-white flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 px-4 py-2 rounded-full shadow-lg shadow-purple-600/50">
                  <Clock size={16} className="text-white animate-pulse" /> 
                  <span className="font-medium tracking-wide">Next reading in <span className="font-mono text-lg font-bold text-white ml-1">{timeUntilReset}</span></span>
              </div>
          </div>
      )}
         <button 
            onClick={openHistory}
            className="absolute right-0 top-0 p-2 text-gray-400 hover:text-white transition-colors flex flex-col items-center gap-1 group"
            title="Reading History"
         >
             <div className="relative">
                 <History size={24} />
             </div>
             <span className="text-[10px] uppercase opacity-0 group-hover:opacity-100 transition-opacity">History</span>
         </button>

        <h1 className="text-4xl font-display font-bold text-white mb-2 flex items-center justify-center gap-3">
          <Moon className="text-[var(--season-secondary)]" /> Mystical Tarot <Sun className="text-[var(--season-primary)]" />
        </h1>
        <p className="text-gray-400">
            {isDaily ? "Your path for now is revealed." : "Reveal a card to guide your path."}
        </p>
      </div>

      <div className="relative w-[280px] h-[460px] md:w-[320px] md:h-[520px] mb-10 perspective-1000 group cursor-pointer" onClick={drawCard}>
        <div 
            className={`w-full h-full relative transition-transform duration-1000 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}
            style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
        >
            <div 
                className={`absolute inset-0 w-full h-full backface-hidden rounded-2xl border-4 border-[var(--season-secondary)]/30 shadow-[0_0_30px_rgba(88,28,135,0.4)] flex items-center justify-center bg-[#1E1B4B] ${!isFlipped && 'animate-breathe'}`}
                style={{ backfaceVisibility: 'hidden' }}
            >
                <div className="absolute inset-2 border-2 border-dashed border-[var(--season-secondary)]/20 rounded-xl"></div>
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[var(--season-primary)] via-transparent to-transparent"></div>
                <Sparkles size={64} className="text-[var(--season-secondary)] animate-pulse opacity-80" />
            </div>

            <div 
                className={`absolute inset-0 bg-[#0F172A] rounded-xl border border-[var(--season-primary)]/50 overflow-hidden backface-hidden flex flex-col shadow-2xl`} 
                style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
            >
                <div className="absolute top-0 w-full text-center py-3 bg-gradient-to-b from-black/80 to-transparent z-10 pointer-events-none">
                    <span className="text-xs font-bold tracking-[0.2em] text-[#FFC82C] uppercase opacity-90 drop-shadow-md">
                        {isDaily ? "Daily Guidance" : "Your Reading"}
                    </span>
                </div>

                <div className="flex-1 flex items-center justify-center pt-8 pb-16 relative bg-[#0B1229] pointer-events-none">
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--season-primary)_0%,_transparent_70%)] opacity-10"></div>
                     {currentCard && (
                         <div className={`transform transition-transform duration-700 ${isReversed ? 'rotate-180' : ''}`}>
                             {getCardIcon(currentCard.id)}
                         </div>
                     )}
                </div>

                {currentCard && (
                    <div className="absolute bottom-0 left-0 w-full min-h-[140px] p-6 bg-gradient-to-t from-black via-[#020410] to-transparent flex flex-col items-center justify-end z-20 pointer-events-none">
                        <div className="flex flex-col items-center mb-3">
                            <h4 className="text-xl font-display font-bold text-white uppercase tracking-wide leading-none mb-1 text-shadow">{currentCard.name}</h4>
                            <span className="text-xs font-mono text-[#FFC82C] opacity-80 uppercase tracking-widest">
                                {isReversed ? 'Reversed' : 'Upright'}
                            </span>
                        </div>
                        <p className="font-display font-medium text-sm leading-relaxed text-gray-200 text-center drop-shadow-[0_2px_2px_rgba(0,0,0,1)]">
                            "{isReversed ? currentCard.meaningReversed : currentCard.meaningUpright}"
                        </p>
                    </div>
                )}
            </div>
        </div>
      </div>

      {isFlipped && currentCard ? (
          <div className="space-y-6 w-full max-w-md animate-in slide-in-from-bottom-4 fade-in duration-500">
              {combinedMessage && (
                  <div className="bg-[var(--season-primary)]/10 border border-[var(--season-primary)]/30 p-4 rounded-xl text-center text-sm text-gray-200">
                      <p className="italic">"{combinedMessage}"</p>
                  </div>
              )}
              
              <div className="flex justify-center gap-3">
                  <button onClick={handleShare} className="flex-1 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 text-white py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70">
                      <Share2 size={18} /> Share
                  </button>
                  <button 
                    onClick={handleDeepReflection} 
                    disabled={isReflecting || (isReflectionLocked && !reflection)} 
                    className={`flex-[2] py-3 rounded-lg flex items-center justify-center gap-2 transition-all font-bold border relative overflow-hidden group
                        ${isReflectionLocked 
                            ? 'bg-gradient-to-r from-purple-900/40 to-pink-900/40 border-gray-700 text-purple-300 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-purple-600 to-pink-500 border-purple-400/50 text-white hover:from-purple-500 hover:to-pink-400 hover:shadow-lg hover:shadow-purple-600/40'}
                    `}
                  >
                      {isReflecting ? <Sparkles className="animate-spin" size={18} /> : <BrainCircuit size={18} />}
                      {isReflectionLocked ? `⏱ ${reflectionTimer}` : (reflection ? "Reflection Unlocked" : "Deep Reflection")}
                  </button>
              </div>

              {reflection && (
                  <div className="bg-card border border-gray-700 p-6 rounded-xl animate-in fade-in zoom-in-95">
                      <h4 className="text-[var(--season-primary)] font-bold mb-2 flex items-center gap-2">
                          <Star size={14}/> ZodiAge Speaks
                      </h4>
                      <div className="text-gray-300 leading-relaxed text-sm space-y-4">
                          {reflection.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                  </div>
              )}

              {!showSpread && isDaily && (
                  <div className="pt-8 border-t border-gray-800">
                      <button 
                        onClick={handleUnlockSpread}
                        className="w-full bg-gradient-to-r from-purple-700 to-pink-600 border border-purple-500/50 hover:border-purple-400 hover:from-purple-600 hover:to-pink-500 text-white py-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all group shadow-lg shadow-purple-600/40"
                      >
                          <div className="flex items-center gap-2 text-white font-bold">
                              <Unlock size={20} /> Unlock Past, Present, Future
                          </div>
                          <p className="text-xs text-gray-500 group-hover:text-gray-300">Dive deeper into today's energy.</p>
                      </button>
                  </div>
              )}
          </div>
      ) : (
        <button 
            onClick={drawCard}
            disabled={isAnimating || (isDaily && isFlipped)}
            className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 text-white font-bold text-lg py-3 px-12 rounded-full shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 animate-pulse-glow"
        >
            {isFlipped ? <><CheckCircle2 size={20} /> Reading Complete</> : <><Sparkles size={20} /> Pick a Card</>}
        </button>
      )}

      {showSpread && (
          <div id="past-present-future" className="mt-16 w-full max-w-4xl border-t border-gray-800 pt-16 animate-in slide-in-from-bottom-10 fade-in pb-12">
              <h3 className="text-3xl font-display font-bold text-center text-white mb-2">Past, Present, Future</h3>
              
              {isSpreadLocked && (
                  <div className="flex flex-col items-center gap-3 mb-8 animate-in fade-in w-full max-w-md mx-auto">
                      <div className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-xl px-6 py-3 shadow-lg shadow-purple-600/50 flex flex-col items-center gap-2">
                          <div className="flex items-center gap-2 text-purple-100">
                              <Clock size={18} className="animate-spin" /> 
                              <span className="font-bold tracking-wide">Next reveal in {spreadTimer}</span>
                          </div>
                          <span className="text-xs text-purple-200 opacity-80">Next available in {spreadTimer}</span>
                      </div>
                  </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
                  {spreadCards.map((item, idx) => {
                      const labels = ["Past", "Present", "Future"];
                      const isRev = spreadRevealed[idx];
                      const activeColor = "text-[#FFC82C]";

                      return (
                        <div key={idx} className="flex flex-col items-center">
                            <div 
                                onClick={() => revealSpreadCard(idx)}
                                onTouchStart={() => revealSpreadCard(idx)}
                                className={`
                                    w-[150px] h-[250px] relative cursor-pointer perspective-1000 group select-none
                                    ${!isRev ? 'hover:-translate-y-2 transition-transform' : ''}
                                `}
                            >
                                <div 
                                    className={`w-full h-full relative transition-transform duration-700 transform-style-preserve-3d`}
                                    style={{ transformStyle: 'preserve-3d', transform: isRev ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
                                >
                                    <div 
                                        className="absolute inset-0 bg-[#1E1B4B] rounded-xl border-2 border-[var(--season-secondary)]/30 flex flex-col items-center justify-center backface-hidden shadow-[0_0_15px_rgba(0,0,0,0.5)] group-hover:border-[var(--season-primary)]/50 transition-colors"
                                        style={{ backfaceVisibility: 'hidden' }}
                                    >
                                        <div className="absolute inset-2 border border-dashed border-[var(--season-secondary)]/20 rounded-lg pointer-events-none"></div>
                                        <div className="text-[10px] font-bold text-[var(--season-secondary)] absolute top-4 uppercase tracking-[0.2em]">{labels[idx]}</div>
                                        <div className="flex flex-col items-center gap-2">
                                            <Sparkles className="text-[var(--season-secondary)] group-hover:text-[var(--season-primary)] opacity-50 transition-colors" size={32} />
                                        </div>
                                    </div>

                                    <div 
                                        className="absolute inset-0 bg-[#0F172A] rounded-xl border border-[var(--season-primary)]/50 overflow-hidden backface-hidden flex flex-col shadow-2xl" 
                                        style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
                                    >
                                        <div className="absolute top-0 w-full text-center py-2 bg-gradient-to-b from-black/80 to-transparent z-10 pointer-events-none">
                                            <span className={`text-[10px] font-bold tracking-[0.2em] uppercase drop-shadow-md ${activeColor}`}>
                                                {labels[idx]}
                                            </span>
                                        </div>

                                        <div className="flex-1 flex items-center justify-center pt-6 pb-12 relative bg-[#0B1229] pointer-events-none">
                                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--season-primary)_0%,_transparent_70%)] opacity-10"></div>
                                            <div className={`transform transition-transform duration-700 scale-[0.65] ${item.reversed ? 'rotate-180' : ''}`}>
                                                {getCardIcon(item.card.id)}
                                            </div>
                                        </div>

                                        <div className="absolute bottom-0 left-0 w-full min-h-[85px] p-3 bg-gradient-to-t from-black via-[#020410] to-transparent flex flex-col items-center justify-end z-20 pointer-events-none">
                                            <div className="flex flex-col items-center mb-1">
                                                <h4 className="text-[10px] font-bold text-white uppercase tracking-wide leading-none mb-0.5 shadow-black text-shadow">{item.card.name}</h4>
                                                <span className={`text-[9px] font-bold uppercase tracking-wider ${activeColor}`}>
                                                    {item.reversed ? 'Reversed' : 'Upright'}
                                                </span>
                                            </div>
                                            <p className="font-display font-medium text-xs leading-tight text-gray-100 text-center drop-shadow-[0_2px_2px_rgba(0,0,0,1)] line-clamp-3">
                                                {item.reversed ? item.card.meaningReversed : item.card.meaningUpright}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                      );
                  })}
              </div>
          </div>
      )}

      {showShareModal && (
          <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowShareModal(false)}>
              <div className="bg-card border border-gray-700 p-6 rounded-2xl max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
                  <h3 className="text-xl font-bold text-white mb-4">Share Your Reading</h3>
                  <div className="bg-[#020410] p-6 rounded-xl border border-[var(--season-primary)]/50 mb-4">
                      <div className="text-[var(--season-primary)] font-bold text-2xl mb-2">{currentCard?.name}</div>
                      <div className="text-gray-400 text-sm mb-4">{isReversed ? 'Reversed' : 'Upright'}</div>
                      <p className="text-gray-200 text-sm italic">"{isReversed ? currentCard?.meaningReversed : currentCard?.meaningUpright}"</p>
                  </div>
                  <button onClick={() => setShowShareModal(false)} className="w-full bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-600 hover:to-indigo-600 text-white py-2 rounded-lg transition-all">Close</button>
              </div>
          </div>
      )}

      {showHistoryModal && (
          <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 backdrop-blur-md" onClick={() => setShowHistoryModal(false)}>
              <div className="bg-[#0B1229] border border-gray-700 p-6 rounded-2xl max-w-lg w-full h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2"><History size={20} className="text-[#FFC82C]" /> Reading History</h3>
                      <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-white"><Unlock size={18}/></button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                      {settings?.readingHistory && settings.readingHistory.length > 0 ? (
                          settings.readingHistory.map((record, i) => {
                              const card = TAROT_DECK.find(c => c.id === record.cardId);
                              if (!card) return null;
                              return (
                                  <div key={i} className="bg-black/40 p-4 rounded-xl border border-gray-800 flex items-center gap-4">
                                      <div className="text-xs text-gray-500 w-20 flex-shrink-0 text-center">
                                          <div>{record.date.split('-').reverse().join('/')}</div>
                                      </div>
                                      <div className="flex-1">
                                          <div className="text-[#FFC82C] font-bold text-sm">{card.name}</div>
                                          <div className="text-xs text-gray-400">{record.reversed ? 'Reversed' : 'Upright'}</div>
                                      </div>
                                  </div>
                              )
                          })
                      ) : (
                          <div className="text-center text-gray-500 mt-10">No history recorded yet.</div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* Educational Content (AdSense Compliance) - Rendered from JSON */}
      <div className="w-full max-w-5xl mx-auto px-4 mb-12 mt-16">
          <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
              <div className="seo-content" dangerouslySetInnerHTML={{ __html: tarotData.html }} />
          </div>
      </div>

            {/* Multiplex Ad for Tarot Page */}
        <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
      </div>
    </>
);
};

export default TarotPage;
